import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import type { Comment, User } from '@vd/types';
import { Avatar } from '../primitives/Avatar';
import { Badge } from '../primitives/Badge';
import { Tooltip } from '../primitives/Tooltip';
import { Menu, MenuItem } from '../primitives/Menu';
import { Popover } from '../primitives/Popover';
import { Button } from '../primitives/Button';
import { cn } from '../cn';
import styles from './CommentCard.module.css';

export interface CommentCardProps {
  comment: Comment;
  /** Resolved author user (for avatar image/role). Optional; derived from comment otherwise. */
  author?: User;
  authorRole?: 'owner' | 'member' | 'viewer';
  /** The comment's time, as the app shows times (TOADO-904); no fallback to the browser's format. */
  formatTime: (iso: string) => ReactNode;
  className?: string;

  /**
   * When true, the card shows an overflow menu with Edit + Delete. Callers
   * decide this based on whoever "owns" the comment — typically `true` only
   * when the current user is the author. MCP-authored comments can still be
   * edited by the human who owns the token; the card doesn't enforce that
   * rule, the parent does.
   */
  canModify?: boolean;
  /** Called with the new body text when the user saves an edit. */
  onSaveEdit?: (body: string) => void | Promise<void>;
  /** Called (no args) when the user confirms deletion. */
  onDelete?: () => void | Promise<void>;
  /**
   * Optional render override for the comment body — lets callers
   * decorate @-mentions, links, etc. while keeping CommentCard
   * decoupled from the host app's rendering helpers. When omitted,
   * the body renders as plain text.
   */
  renderBody?: (body: string) => ReactNode;
}

export function CommentCard({
  comment, author, authorRole, formatTime, className,
  canModify, onSaveEdit, onDelete, renderBody,
}: CommentCardProps) {
  const isMcp = comment.authorType === 'mcp_client';
  const name = author?.name ?? comment.authorDisplayName;
  const timeText = formatTime(comment.createdAt);
  const wasEdited = !!comment.updatedAt;

  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const editRef = useRef<HTMLTextAreaElement>(null);
  const moreBtn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (editing) {
      editRef.current?.focus();
      editRef.current?.setSelectionRange(draft.length, draft.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const openMenu = () => setMenuAnchor(moreBtn.current?.getBoundingClientRect() ?? null);
  const closeMenu = () => setMenuAnchor(null);

  const startEdit = () => { setDraft(comment.body); setEditing(true); closeMenu(); };
  const cancelEdit = () => { setEditing(false); setDraft(comment.body); };
  const saveEdit = async () => {
    const next = draft.trim();
    if (!next) return;
    if (next === comment.body) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSaveEdit?.(next);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = () => { setConfirmingDelete(true); closeMenu(); };
  const cancelDelete = () => setConfirmingDelete(false);
  const doDelete = async () => {
    try { await onDelete?.(); }
    finally { setConfirmingDelete(false); }
  };

  return (
    <div className={cn(styles.comment, isMcp && styles.mcp, className)} data-testid="comment-card">
      <Avatar name={name} src={author?.avatarUrl} size="md" bot={isMcp} />
      <div className={styles.body}>
        <div className={styles.head}>
          <span className={styles.author}>{name}</span>
          {isMcp ? (
            <Tooltip content={`Posted via MCP token "${comment.authorTokenLabel ?? 'unknown'}"`}>
              <Badge variant="mcp">★ MCP</Badge>
            </Tooltip>
          ) : (
            authorRole && <Badge variant="outline">{authorRole === 'owner' ? 'Owner' : authorRole === 'viewer' ? 'Viewer' : 'Member'}</Badge>
          )}
          <span className={styles.time}>
            {timeText}
            {wasEdited && <span className={styles.edited}> · edited</span>}
          </span>
          {canModify && !editing && (
            <button
              ref={moreBtn}
              className={styles.moreBtn}
              onClick={openMenu}
              aria-label="Comment actions"
              data-testid="comment-more"
            >
              <MoreHorizontal size={14} />
            </button>
          )}
        </div>

        {editing ? (
          <div className={styles.editor}>
            <textarea
              ref={editRef}
              className={styles.editorTextarea}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); void saveEdit(); }
              }}
              data-testid="comment-edit-input"
            />
            <div className={styles.editorActions}>
              <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving}>Cancel</Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void saveEdit()}
                disabled={saving || !draft.trim() || draft.trim() === comment.body}
                loading={saving}
                data-testid="comment-edit-save"
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.text}>{renderBody ? renderBody(comment.body) : comment.body}</div>
        )}

        {confirmingDelete && (
          <div className={styles.confirm} data-testid="comment-delete-confirm">
            Delete this comment?
            <div className={styles.confirmActions}>
              <Button variant="ghost" size="sm" onClick={cancelDelete}>Cancel</Button>
              <Button variant="destructive" size="sm" onClick={() => void doDelete()} data-testid="comment-delete-confirm-yes">
                Delete
              </Button>
            </div>
          </div>
        )}
      </div>

      <Popover open={!!menuAnchor} onClose={closeMenu} anchor={menuAnchor} placement="bottom-end">
        <Menu>
          <MenuItem leftIcon={<Pencil size={14} />} onClick={startEdit}>Edit</MenuItem>
          <MenuItem leftIcon={<Trash2 size={14} />} onClick={confirmDelete}>Delete</MenuItem>
        </Menu>
      </Popover>
    </div>
  );
}
